import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  Grid,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Alert from "@mui/material/Alert";
import { decoratedFetch } from "../../req_utils";
import { SUPPORT_EMAIL } from "../../constants";
import { useSelector } from "react-redux";

const InventoryItemDialog = ({
  isOpen,
  inventoryItem,
  handleClose,
  setSnackbarOpen,
  setSnackbarMessage,
}) => {
  const employee = useSelector((state) => state.employee);
  const employee_branch_id = useSelector((state) => state.employee_branch_id);
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [types, setTypes] = useState([]);
  const [vendor, setVendor] = useState("");
  const [vendors, setVendors] = useState([]);
  const [style, setStyle] = useState("");
  const [styles, setStyles] = useState([]);
  const [color, setColor] = useState("");
  const [colors, setColors] = useState([]);
  const [size, setSize] = useState("");
  const [unit, setUnit] = useState("");
  const [units, setUnits] = useState([]);
  const [quantity, setQuantity] = useState("");
  const [reorderThreshold, setReorderThreshold] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [alertMessage, setAlertMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const resetFields = () => {
    setName("");
    setType("");
    setVendor("");
    setStyle("");
    setColor("");
    setSize("");
    setUnit("");
    setQuantity("");
    setReorderThreshold("");
    setUnitCost("");
    setLocation("");
    setNotes("");
    setSelectedBranch(employee_branch_id || employee?.branch_id || "");
    setLoading(false);
    setAlertMessage("");
  };

  const initializeFromInventoryItemProp = () => {
    setName(inventoryItem?.name || "");
    setType(inventoryItem?.material_type || "");
    setVendor(inventoryItem?.material_vendor || "");
    setStyle(inventoryItem?.material_style || "");
    setColor(inventoryItem?.material_color || "");
    setSize(inventoryItem?.material_size || "");
    setUnit(inventoryItem?.size_unit?.id || "");
    setQuantity(inventoryItem?.quantity ?? "");
    setReorderThreshold(inventoryItem?.reorder_threshold ?? "");
    setUnitCost(inventoryItem?.unit_cost ?? "");
    setLocation(inventoryItem?.location || "");
    setNotes(inventoryItem?.notes || "");
    setSelectedBranch(inventoryItem?.branch_id || "");
    setLoading(false);
    setAlertMessage("");
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (inventoryItem) {
      initializeFromInventoryItemProp();
    } else {
      resetFields();
    }
    decoratedFetch("/list_material_types")
      .then((response) => response.json())
      .then((data) => setTypes(data))
      .catch((error) =>
        console.error(`Error fetching material types ${error}`),
      );
    decoratedFetch("/list_size_units")
      .then((response) => response.json())
      .then((data) => setUnits(data))
      .catch((error) => console.error(`Error fetching size units ${error}`));
    decoratedFetch("/list_material_vendors")
      .then((response) => response.json())
      .then((data) => setVendors(data))
      .catch((error) =>
        console.error(`Error fetching material vendors ${error}`),
      );
    decoratedFetch("/list_material_colors")
      .then((response) => response.json())
      .then((data) => setColors(data))
      .catch((error) =>
        console.error(`Error fetching material colors ${error}`),
      );
    decoratedFetch("/list_material_styles")
      .then((response) => response.json())
      .then((data) => setStyles(data))
      .catch((error) =>
        console.error(`Error fetching material styles ${error}`),
      );
    decoratedFetch("/list_branch_addresses")
      .then((response) => response.json())
      .then((data) => setBranches(data))
      .catch((error) => console.error(`Error fetching branches: ${error}`));
  }, [isOpen]);

  const handleDelete = () => {
    setLoading(true);
    decoratedFetch(`/delete_inventory_item/${inventoryItem.id}`, {
      method: "DELETE",
    })
      .then((response) => {
        if (response.status === 200) {
          setLoading(false);
          handleClose();
          setSnackbarOpen(true);
          setSnackbarMessage("Inventory item deleted");
          return;
        }
        return response.json().then((errorPayload) => {
          setAlertMessage(
            errorPayload.msg ||
              `Failed to delete inventory item. If this persists, please contact ${SUPPORT_EMAIL}`,
          );
          throw new Error();
        });
      })
      .catch((error) => {
        console.error(`Error deleting inventory item: ${error}`);
        setLoading(false);
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    if (!name || !unit || !selectedBranch) {
      setAlertMessage(
        "Required fields not filled in. Items must have a name, unit and branch",
      );
      setLoading(false);
      return;
    }
    const api = inventoryItem
      ? `/update_inventory_item/${inventoryItem.id}`
      : "/create_inventory_item";
    decoratedFetch(api, {
      method: inventoryItem ? "PUT" : "POST",
      body: JSON.stringify({
        name: name,
        material_type: type,
        material_vendor: vendor,
        material_style: style,
        material_color: color,
        material_size: size,
        size_unit_id: unit,
        quantity: quantity || "0",
        reorder_threshold: reorderThreshold,
        unit_cost: unitCost,
        location: location,
        notes: notes,
        branch_id: selectedBranch,
      }),
    })
      .then((response) => {
        if (!inventoryItem && response.status === 201) {
          return;
        }
        if (inventoryItem && response.status === 200) {
          return;
        }
        if (response.status === 400) {
          return response.json().then((errorPayload) => {
            setAlertMessage(errorPayload.msg || "Invalid inventory item");
            throw new Error();
          });
        }
        setAlertMessage(
          `Internal server error. If this persists, please contact ${SUPPORT_EMAIL}`,
        );
        throw new Error();
      })
      .then(() => {
        setLoading(false);
        handleClose();
        setSnackbarOpen(true);
        setSnackbarMessage(
          `Inventory item ${inventoryItem ? "updated" : "created"}`,
        );
      })
      .catch((error) => {
        console.error(
          `Error ${inventoryItem ? "editing" : "creating"} inventory item: ${error}`,
        );
        setLoading(false);
      });
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage("");
  };

  return (
    <Dialog
      disableScrollLock
      open={isOpen}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": { minWidth: "650px" },
      }}
    >
      <DialogTitle sx={{ fontSize: "22px", paddingBottom: "0px" }}>
        {inventoryItem ? "Edit Inventory Item" : "Create Inventory Item"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} direction="row" sx={{ marginTop: "2px" }}>
          {alertMessage && (
            <Grid item xs={12}>
              <Alert
                onClose={handleAlertClose}
                severity="error"
                sx={{ width: "100%", borderRadius: 8 }}
              >
                {alertMessage}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              label="Name"
              required
              fullWidth
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="Type"
              value={type}
              freeSolo
              options={types.map((option) => option.display_name)}
              renderInput={(params) => <TextField {...params} label="Type" />}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                  setType(newInputValue);
                }
              }}
              onChange={(event, newValue) => {
                if (typeof newValue === "string") {
                  setType(newValue);
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="Vendor"
              value={vendor}
              freeSolo
              options={vendors.map((option) => option.display_name)}
              renderInput={(params) => (
                <TextField {...params} label="Manufacturer" />
              )}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                  setVendor(newInputValue);
                }
              }}
              onChange={(event, newValue) => {
                if (typeof newValue === "string") {
                  setVendor(newValue);
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="Style"
              value={style}
              freeSolo
              options={styles.map((option) => option.display_name)}
              renderInput={(params) => (
                <TextField {...params} label="Product" />
              )}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                  setStyle(newInputValue);
                }
              }}
              onChange={(event, newValue) => {
                if (typeof newValue === "string") {
                  setStyle(newValue);
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="Color"
              value={color}
              freeSolo
              options={colors.map((option) => option.display_name)}
              renderInput={(params) => <TextField {...params} label="Color" />}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                  setColor(newInputValue);
                }
              }}
              onChange={(event, newValue) => {
                if (typeof newValue === "string") {
                  setColor(newValue);
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Size"
              fullWidth
              variant="outlined"
              value={size}
              onChange={(e) => setSize(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl variant="outlined" sx={{ width: "100%" }} required>
              <InputLabel>Unit</InputLabel>
              <Select
                id="unit-select"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                label="Unit"
                fullWidth
              >
                {units.map((sizeUnit) => (
                  <MenuItem key={sizeUnit.id} value={sizeUnit.id}>
                    {sizeUnit.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={inventoryItem ? "On Hand" : "Starting Quantity"}
              type="number"
              fullWidth
              value={quantity}
              disabled={!!inventoryItem}
              onChange={(e) => setQuantity(e.target.value)}
              helperText={
                inventoryItem ? "Change stock with Adjust Stock" : undefined
              }
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Reorder At"
              type="number"
              fullWidth
              value={reorderThreshold}
              onChange={(e) => setReorderThreshold(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Unit Cost"
              type="number"
              fullWidth
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Location"
              fullWidth
              variant="outlined"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth required>
              <InputLabel>Branch</InputLabel>
              <Select
                id="branch-select"
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                label="Branch"
                disabled={!!inventoryItem}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch?.id} value={branch?.id}>
                    {branch?.name || branch?.state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="notes"
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: "flex-end", margin: "5px", marginRight: "15px" }}
      >
        {loading ? (
          <CircularProgress
            size="25px"
            sx={{ textAlign: "center", mt: "6px", mb: "5.5px", mr: "20px" }}
          />
        ) : (
          <div>
            <Button
              sx={{ marginRight: "10px" }}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            {inventoryItem && (
              <Button
                style={{ marginRight: 10 }}
                variant="contained"
                color="secondary"
                onClick={handleDelete}
                disabled={alertMessage}
              >
                Delete
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={alertMessage}
            >
              {inventoryItem ? "Save" : "Create"}
            </Button>
          </div>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InventoryItemDialog;
